<!-- 
Belstaff internal feature
UI component for requesting a back in stock notification
 -->
<script context="module" lang="ts">
  export type SubscribeStatus = "idle" | "sending" | "error" | "sent";
</script>

<script lang="ts">
  import { _, locale } from "svelte-i18n";
  import { createEventDispatcher } from "svelte";

  import Typography from "$lib/components/Typography/Typography.svelte";
  import InputField from "$lib/components/InputField/InputField.svelte";
  import { regionStore } from "$lib/stores/region";
  import Checkbox from "$lib/components/Checkbox/Checkbox.svelte";
  import { subscribeToNewsletter } from "$lib/utils/subscribeToNewsletter";
  import RadioGroup from "$lib/components/RadioGroup/RadioGroup.svelte";

  import type { Page } from "@sveltejs/kit";
  import type { Readable } from "svelte/store";
  import { page as pageStore } from "$app/stores";
  import { getApiUrl } from "$lib/utils/url";
  import { modals } from "$lib/stores/modal";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { linkTo } from "$lib/utils/url";

  const regionInfo = regionStore();

  export let page: Readable<Page> = pageStore;
  export let variantId: string;
  export let withPadding = false;
  export let closeButton = false;

  $: variantId,
    ((subscribeStatus = defaultSubscribeStatus),
    (email = ""),
    (errorMessage = null));

  export let language =
    $regionInfo && $regionInfo.locale && $regionInfo.locale.toUpperCase();

  export let formTitle = "Back in stock Form";
  export let email = "";

  let gender = "undisclosed";
  let honeypot;

  export let newsletterEmailError = false;
  export let signupSuccess: boolean | undefined;
  export let defaultSubscribeStatus: SubscribeStatus = "idle";

  let bisForm: HTMLFormElement;

  let subscribeStatus: SubscribeStatus;
  let errorMessage: string | null;

  $: subscribeStatus = defaultSubscribeStatus;
  $: errorMessage = null;

  let newsletter = false;

  const url = getApiUrl($page, "backinstock");

  const submitHandler = async () => {
    let lang = $locale;
    lang = lang && lang.split("-")[0];
    if (subscribeStatus === "sending") return;

    if (newsletter === true) {
      subscribeToNewsletter(email, gender, honeypot, lang).then(res => {
        newsletterEmailError = res.emailError;
        errorMessage = $_(res.errorMessage);
        signupSuccess = res.signupSuccess;
      });
    }

    if (signupSuccess === false || newsletterEmailError === true) {
      subscribeStatus = "error";
    } else {
      errorMessage = null;
      subscribeStatus = "sending";

      const result = await fetch(
        `${url}?email=${encodeURIComponent(
          email,
        )}&variantId=${encodeURIComponent(variantId)}&language=${language}`,
        { method: "POST" },
      );

      const { success, error } = await result.json();
      subscribeStatus = success ? "sent" : "error";
      if (error) {
        errorMessage = $_("register.error");
      }
    }
  };

  const dispatch = createEventDispatcher();

  const close = () => {
    dispatch("close");
  };
</script>

<style lang="scss">
  .newsletter-signup {
    &--check-wrapper {
      position: absolute;
      left: -9999px;
      visibility: hidden;
    }

    &--gender {
      display: flex;
      align-items: flex-start;
      margin-top: 22px;
    }
  }
  .accordion {
    &--padded {
      padding: 24px 32px;

      @include breakpoint(md) {
        padding: 24px 44px;
      }

      @include breakpoint(lg) {
        padding: 16px;
      }
    }

    &--close {
      background-color: transparent;
      border: none;
      position: absolute;
      top: 0;
      right: 0;
      padding: 8px;
    }

    .bis--form {
      padding-top: 24px;
    }

    .input-field-container {
      padding-top: 24px;
    }
  }

  .inline-anchor {
    cursor: pointer;
  }

  :global(.input-field-container label input.checkbox) {
    min-width: 20px;
    max-height: 20px;
  }

  :global(.bis--form .input-field-container .checkbox input) {
    border-radius: 14px;
  }

  :global(.bis--form .input-field-container .checkbox input:checked::before) {
    display: none;
  }
</style>

<div class={`accordion ${withPadding && "accordion--padded"} `}>
  {#if closeButton}
    <button class="accordion--close" on:click={close}>
      <Icon icon="close" />
    </button>
  {/if}
  <div>
    {#if subscribeStatus === "sent"}
      <Typography component="h3" theme="on-background" variant="button1"
        >{$_("backinstock.complete")}</Typography
      >{:else}
      <Typography component="h3" theme="on-background" variant="button1"
        >{$_("backinstock.header")}</Typography
      >
    {/if}
    <form
      class="bis--form"
      title={formTitle}
      bind:this={bisForm}
      on:submit|preventDefault={submitHandler}
    >
      {#if subscribeStatus !== "sent"}
        <Typography component="p" variant="body1">
          {$_("backinstock.body")}</Typography
        >

        <div class="input-field-container">
          <Checkbox
            label={$_("backinstock.newsletter")}
            placeholder={$_("backinstock.newsletter")}
            type="checkbox"
            bind:checked={newsletter}
          />
        </div>
        <div class="newsletter-signup--check-wrapper">
          <InputField
            type="text"
            name="name"
            bind:value={honeypot}
            fullWidth={true}
            tabindex={-1}
            autocomplete="off"
          />
        </div>
        <div class="input-field-container">
          <InputField
            placeholder={`${$_("Email")} *`}
            label={$_("Email")}
            type="email"
            name="email"
            fullWidth
            required
            bind:value={email}
            error={errorMessage !== null}
            {errorMessage}
            inlineSubmitBtn={true}
          />
        </div>
        {#if newsletter}
          <div
            class="newsletter-signup--gender"
            style={errorMessage && "margin-top: 0px"}
          >
            <RadioGroup
              items={[
                { label: $_("men"), name: "men" },
                { label: $_("women"), name: "women" },
              ]}
              bind:group={gender}
              margin={"0 24px 0 0"}
            />
            <Typography component="p" variant="body1" theme="on-surface">
              <a
                href={linkTo("legal/privacy-policy")}
                on:click|preventDefault={() => {
                  modals.open("privacyPolicy");
                }}
                class="inline-anchor"
              >
                {$_("Privacy Policy")}</a
              >
            </Typography>
          </div>
        {/if}
      {/if}
    </form>
  </div>
</div>
